/* eslint-disable no-prototype-builtins */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import parse from 'parse-json';
import JsonQuery from 'json-query';
import HighchartsReact from 'highcharts-react-official';
import { merge } from 'object-path-immutable';
import _ from 'lodash';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getColorPalette,
    getTextSchemaProperties,
    rebaseGrowthChart,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import { defaultChartButtons } from './defaultChartButtons';
import stockChartStyles, { themeStockChartStyles } from './StockChart.styles';

interface SeriesPoint extends Highcharts.TooltipFormatterContextObject {
    distribution: number;
}

interface highChartOptions {
    path?: string;
    value?: string;
}

interface StockChartProps {
    options: highChartOptions[];
    dataKey: string;
    chartType: string;
    chartTitles: Headings[];
    legendDropDownTitle: string;
    showGrowthAmount: boolean;
    growthAmountLabel: string;
    initialGrowthAmount: number;
    rangeButtons: string;
    xyKeys: string;
    showDistributions: boolean;
    distributionKey: string;
    toolTipValueFormat: string;
    dollarChangeValueFormat: string;
    isShowBenchMarks: boolean;
    seriesColors: string;
    isGrowthChart: boolean;
    addOffsetMinToYtd: boolean;
    typographyClass?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, StockChartProps {}

const StockChart = (props: commonTypes.WidgetInstanceProps) => {
    const { documentType } = commonHooks.useAppContext();
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;
    const { styles, options, dataKey, chartTitles, chartType, ...rest } = widgetProperties || props;
    const [growthAmountFeedback, setGrowthAmountFeedback] = useState('');
    const [highstock, setHighstock] = useState<any>(null);

    const i18n = commonHooks.useLocalization();

    const data: any = props.data;
    const theme = data?.themeSelected || props?.theme;
    const stockChartRef = useRef<any>(null);
    const minX = 0;

    const chartButtons: string = rest.rangeButtons
        ? JsonQuery(rest.rangeButtons as string, { data }).value
        : defaultChartButtons;
    let _chartButtons: any = parse(chartButtons || '[]');

    if (rest.addOffsetMinToYtd) {
        const ytdIndex = _chartButtons.findIndex((c: any) => c.type === 'ytd');
        const ytd = {
            type: 'ytd',
            text: _chartButtons[ytdIndex]?.text,
            title: _chartButtons[ytdIndex]?.title,
            offsetMin: -24 * 3600 * 1000,
        };
        _chartButtons = _chartButtons.map((d: any, i: any) => {
            if (i === ytdIndex) {
                return ytd;
            } else {
                return d;
            }
        });
    }

    const chartData: any = dataKey ? _.cloneDeep(JsonQuery(`${dataKey}`, { data }).value) : null;

    if (rest.isShowBenchMarks) {
        const benchMarks = data?.overview?.benchmarks?.filter(
            (b: any) => Array.isArray(b.prices) && b.prices.length
        );
        const currentSeries: any =
            Array.isArray(chartData) && chartData.filter((d) => d.isCurrent)[0];
        // current series start date
        const _date = currentSeries?.values?.[0]?.date;

        if (_date) {
            benchMarks?.forEach((b: any) => {
                const _navps: number = _.result(
                    _.find(b.prices, function (obj) {
                        return obj.effectiveDate === _date;
                    }),
                    'navps'
                );
                const rebasedValue = 10000 / _navps;
                const values: any = [];
                _.isArray(currentSeries?.values) &&
                    currentSeries.values?.forEach((s: any) => {
                        const navps: number = _.result(
                            _.find(b.prices, function (obj) {
                                return obj.effectiveDate === s?.date;
                            }),
                            'navps'
                        );

                        if (navps) {
                            values.push({
                                date: s?.date,
                                value: navps * rebasedValue,
                            });
                        } else {
                            // Get the value of last weekday if current series
                            // date falls under weekend
                            const dateObject = new Date(s?.date);
                            let yesterdayDate = new Date(dateObject?.getTime());
                            yesterdayDate.setDate(dateObject.getDate() - 1);

                            yesterdayDate = new Date(
                                yesterdayDate.getTime() -
                                    yesterdayDate.getTimezoneOffset() * 60 * 1000
                            );

                            const yesterdayGrowthDate = yesterdayDate.toISOString().split('T')[0];

                            let dayBeforeDate = new Date(dateObject.getTime());
                            dayBeforeDate.setDate(dateObject.getDate() - 2);

                            dayBeforeDate = new Date(
                                dayBeforeDate.getTime() -
                                    dayBeforeDate.getTimezoneOffset() * 60 * 1000
                            );

                            const dayBeforeGrowthDate = dayBeforeDate.toISOString().split('T')[0];

                            const dayBeforeNavps: number = _.result(
                                _.find(b.prices, function (obj) {
                                    return obj.effectiveDate === yesterdayGrowthDate;
                                }),
                                'navps'
                            );

                            if (dayBeforeNavps) {
                                values.push({
                                    date: s?.date,
                                    value: dayBeforeNavps * rebasedValue,
                                });
                            } else {
                                const twoDayBeforeNavps: number = _.result(
                                    _.find(b.prices, function (obj) {
                                        return obj.effectiveDate === dayBeforeGrowthDate;
                                    }),
                                    'navps'
                                );
                                twoDayBeforeNavps &&
                                    values.push({
                                        date: s?.date,
                                        value: twoDayBeforeNavps * rebasedValue,
                                    });
                            }
                        }
                    });

                Array.isArray(chartData) &&
                    chartData?.push({
                        ...b,
                        values,
                        isVisible: false,
                        isCurrent: false,
                        type: 'SERIES',
                    });
            });
        }
    }

    const prepareData = (rawData: any) => {
        const xyKeys = rest?.xyKeys?.split(',');
        const x = xyKeys?.[0];
        const y = xyKeys?.[1];
        let distributionsData: any = [];

        const getDate = (o: any) => {
            const d = o?.[x];
            const splitDate = d?.split('-');
            const year = splitDate?.[0];
            const month = splitDate?.[1] - 1;
            const day = splitDate?.[2];
            const date = Date.UTC(year, month, day);

            return date;
        };

        const seriesData =
            rawData.length > 0 &&
            rawData?.map((r: any, i: number) => {
                const rawDistributions: any = [];

                r?.values?.forEach((o: any) => {
                    if (rest.showDistributions && !!o[rest.distributionKey]) {
                        rawDistributions.push({
                            x: getDate(o),
                            distribution: o[rest.distributionKey],
                        });
                    }
                });

                let _values = r?.values;

                // Rebasing values based on performance reset date
                if (rest?.isGrowthChart && data?.overview?.performanceResetDate) {
                    const filteredValues = r?.values?.filter(
                        (v: any) =>
                            new Date(v?.date) >= new Date(data?.overview?.performanceResetDate)
                    );

                    const scale = 10000 / filteredValues[0]?.value;
                    _values = filteredValues.map((v: any) => ({ ...v, value: v.value * scale }));
                }

                const preparedData = _values?.map((o: any) => ({
                    x: getDate(o),
                    y: o[y],
                    distribution: o[rest.distributionKey],
                }));

                if (rawDistributions && rawDistributions.length > 0) {
                    distributionsData = [
                        ...distributionsData,
                        {
                            type: 'flags',
                            name: r.name,
                            isCurrent: r.isCurrent,
                            data: rawDistributions,
                            visible: r.isCurrent || i === 0,
                        },
                    ];
                }

                return {
                    name: r.name,
                    isCurrent: r.isCurrent,
                    data: preparedData,
                    visible: r.isCurrent || i === 0,
                };
            });

        return [...seriesData, ...distributionsData];
    };

    const preparedData = !_.isEmpty(chartData) ? prepareData(chartData) : null;

    const parsedData = parse(JSON.stringify(preparedData));

    let seriesColors: any;

    try {
        seriesColors = parse(rest.seriesColors || '[]');
    } catch (error) {
        seriesColors = [];
    }

    // transform and choose which series to show
    const seriesData: any =
        _.isArray(preparedData) &&
        parsedData.map((series: any, index: number) => ({
            ...series,
            type: series.type !== 'flags' ? chartType : series.type,
            data: series.data,
            ...seriesColors[index],
        }));

    const stockChartId = uuidv4();

    useEffect(() => {
        import('highcharts/highstock').then((highstock) => {
            setHighstock(highstock);

            // render few chart options on initial render
            highstock.setOptions({
                lang: {
                    rangeSelectorZoom: '',
                    rangeSelectorFrom: '',
                    rangeSelectorTo: '-',
                },
            });
        });
    }, []);

    let highChartOptions: Highcharts.Options = {
        chart: {
            height: 300,
            events: {
                render: function () {
                    const chart = this;
                    const points: any[] = [];

                    chart.series.forEach((s) => {
                        if (s.visible && s.type !== 'flags') {
                            s.points.length && points.push(s.points?.[s.points.length - 1]);
                        }
                    });

                    points.filter((p) => !!p).length > 0 && chart?.tooltip?.refresh(points);
                },
            },
        },
        title: {
            text: '',
        },
        rangeSelector: {
            verticalAlign: 'bottom',
            inputDateFormat: '%m/%d/%Y',
            inputEditDateFormat: '%m/%d/%Y',
            x: 0,
            y: 0,
            selected: 0,
            buttons: _chartButtons,
            buttonPosition: { align: 'right' },
            inputPosition: { align: 'left', y: 30 },
            buttonTheme: {
                width: 38,
                height: 16,
                fill: !theme ? 'none' : 'var(--color-stock-chart-range-button-fill)',
                stroke: !theme ? 'darkgray' : 'var(--color-stock-chart-range-button-stroke)',
                'stroke-width': 1,
                style: {
                    fontSize: !theme ? '8pt' : 'var(--font-size-xx-small)',
                    fontFamily: !theme ? '' : 'var(--font-family-regular)',
                },
                states: {
                    hover: {
                        fill: !theme ? 'grey' : 'var(--color-stock-chart-range-button-hover)',
                    },
                    select: {
                        fill: !theme
                            ? 'darkgrey'
                            : 'var(--color-stock-chart-range-button-selected)',
                    },
                    disabled: {
                        stroke: !theme
                            ? 'lightgrey'
                            : 'var(--color-stock-chart-range-button-disabled)',
                    },
                },
            },
            inputStyle: {
                color: '#000',
                textDecoration: 'underline solid #000 2px',
            },
        },
        navigator: { enabled: false },
        credits: { enabled: false },
        colors: getColorPalette(),
        xAxis: {
            visible: false,
            type: 'datetime',
            minRange: 1000 * 60 * 60 * 24 * 30,
            events: {
                setExtremes(e) {
                    if (rest.isGrowthChart) {
                        seriesData
                            .filter((s: any) => s.type !== 'flags')
                            .forEach((series: any, i: number) => {
                                const firstPointIndex = series.data.findIndex(
                                    (point: any) => point.x >= e.min
                                );
                                // get the first point after adjust the nav
                                const firstPoint = series.data[firstPointIndex];

                                if (firstPoint === undefined) return;

                                // eslint-disable-next-line no-extra-parens
                                const growthAmount: string =
                                    (
                                        document.getElementById(
                                            `${stockChartId}-growth-amount-input`
                                        ) as HTMLInputElement
                                    )?.value || rest.initialGrowthAmount.toString();

                                const rebasedData = rebaseGrowthChart(
                                    parseFloat(growthAmount),
                                    series.data,
                                    firstPoint
                                ).slice();
                                // update the chart with the rebased values
                                this.chart?.series[i].update({
                                    data: rebasedData,
                                    type: 'area',
                                });
                            });
                    }
                },
            },
        },
        yAxis: {
            gridLineWidth: 0,
            labels: {
                x: -10,
                formatter: function (this: any) {
                    const formattedYValue = rest.toolTipValueFormat
                        ? i18n.t(this.value, rest.toolTipValueFormat)
                        : this.value;
                    return formattedYValue?.toString();
                },
            },
        },
        plotOptions: {
            series: {
                turboThreshold: 5000,
                marker: {
                    fillColor: 'var(--color-stock-chart-marker)',
                },
                states: {
                    hover: {
                        enabled: true,
                        halo: {
                            size: 0,
                        },
                    },
                },
            },
            area: {
                threshold: null,
                lineColor: 'var(--color-stock-chart-line)',
                lineWidth: 1,
                fillColor: {
                    linearGradient: {
                        x1: 0,
                        y1: 0,
                        x2: 0,
                        y2: 1,
                    },
                    stops: [
                        [0, 'var(--color-stock-chart-fill-01)'],
                        [1, 'var(--color-stock-chart-fill-02)'],
                    ],
                },
            },
            flags: {
                title: 'D',
                shape: 'circlepin',
                showInLegend: false,
                fillColor: 'var(--color-stock-chart-flag-fill)',
                opacity: 1,
                width: 15,
                height: 15,
                cursor: 'pointer',
                style: {
                    color: 'var(--color-white)',
                },
                states: {
                    hover: {
                        fillColor: 'var(--color-stock-chart-flag-hover-fill)',
                    },
                },
            },
        },
        scrollbar: {
            enabled: false,
        },
        tooltip: {
            shared: true,
            followPointer: false,
            followTouchMove: false,
            positioner: function () {
                return { x: 0, y: 25 };
            },
            shadow: false,
            split: false,
            useHTML: true,
            formatter: function () {
                let pointFormat = '';
                let isFlagSeries = false;

                if (this.hasOwnProperty('series') && this.series.options.type === 'flags') {
                    isFlagSeries = true;
                }

                const points = this.points;

                const xDate = new Date(this.x || '');
                xDate.setDate(xDate.getDate() + 1);
                const x = i18n.t(xDate.toUTCString(), 'dateShort');

                if (!isFlagSeries && (!points || !points.length)) return false;

                const point = !isFlagSeries
                    ? (points?.[0] as SeriesPoint)
                    : (this.point as Highcharts.Point & {
                          distribution: number;
                      });

                if (isFlagSeries) {
                    const pointDistribution: any = point.distribution;
                    pointFormat = `
                        <div class="custom-point-container">
                            <span class='price-value'>
                                ${i18n.t(pointDistribution, 'curMax2')}
                            </span>
                            <span class='date'>${x}</span>
                        </div>
                    `;
                } else {
                    const pointY: any = point.y;
                    const seriesData = point.series.data;
                    const index = seriesData.findIndex((d) => d && d.x === point.x);
                    const prevPointY = seriesData[index - 1]?.y || 0;

                    const totalPrice: number = point.distribution
                        ? point.distribution + pointY
                        : pointY;
                    const priceDiffValue: any = totalPrice - prevPointY;
                    const priceDiffPct: any =
                        (totalPrice - prevPointY) / ((totalPrice + prevPointY) / 2);

                    const formattedYValue = rest.toolTipValueFormat
                        ? i18n.t(pointY, rest.toolTipValueFormat)
                        : pointY;
                    const _priceDiffValue = i18n.t(
                        priceDiffValue,
                        rest.dollarChangeValueFormat || 'curDiffMax2'
                    );

                    if (!rest.isGrowthChart) {
                        pointFormat = `
                            <div class="custom-point-container">
                                <span class='price-value'>${formattedYValue}</span>
                                <span class='price-diffrence'>
                                    ${i18n.t(priceDiffPct, 'pctDiffMax2')}
                                    (${
                                        typeof _priceDiffValue === 'string'
                                            ? _priceDiffValue.replace('US', '')
                                            : ''
                                    })
                                </span>
                                <span class='date'>${x}</span>
                            </div>
                        `;
                    } else {
                        const growthAmount: string =
                            // eslint-disable-next-line no-extra-parens
                            (
                                document.getElementById(
                                    `${stockChartId}-growth-amount-input`
                                ) as HTMLInputElement
                            )?.value || rest.initialGrowthAmount.toString();

                        const priceDiffPctFromStart: any =
                            (pointY - parseFloat(growthAmount)) / parseFloat(growthAmount);
                        const priceDiffValueFromStart: any = pointY - parseFloat(growthAmount);
                        const _priceDiffValueFromStart: any = i18n.t(
                            priceDiffValueFromStart,
                            rest.dollarChangeValueFormat || 'curDiffMax2'
                        );
                        pointFormat = `
                            <div class="custom-point-container">
                                <span class='price-value'>${formattedYValue}</span>
                                <span class='price-diffrence'>
                                    ${i18n.t(priceDiffPctFromStart, 'pctDiffMax2')}
                                    (${
                                        typeof _priceDiffValueFromStart === 'string'
                                            ? _priceDiffValueFromStart.replace('US', '')
                                            : ''
                                    })
                                </span>
                                <span class='date'>${x}</span>
                            </div>
                        `;
                    }
                }

                return pointFormat;
            },
        },
        series: seriesData,
        legend: { align: 'right' },
    };

    Array.isArray(options) &&
        options.forEach((o) => {
            let userOptions;

            try {
                userOptions = parse(o.value || '{}');
            } catch (error) {
                userOptions = {};
            }

            highChartOptions = merge(highChartOptions, o.path, userOptions);
        });

    const onChangeSeriesSelected = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const index = e?.target?.value;
        const series = stockChartRef?.current?.chart?.series;
        const selectedSeries = series[index];
        const selectedSeriesName = selectedSeries?.name;
        const areaSeries = series?.filter(
            (s: any) => s.type === 'area' && !s.userOptions?.isCurrent
        );
        const flagsSeries = series?.filter(
            (s: any) => s.type === 'flags' && !s.userOptions?.isCurrent
        );

        areaSeries.forEach((s: Highcharts.Series) => {
            if (s.name === selectedSeriesName) {
                s.setVisible(true, true);
                s.setState('', true);
            } else if (s.visible) {
                s.setVisible(false, false);
            }
        });

        flagsSeries.forEach((s: Highcharts.Series) => {
            if (s.name === selectedSeriesName) {
                s.setVisible(true, true);
            } else {
                s.setVisible(false, false);
            }
        });
    };

    const onChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input: number = parseFloat(e.target.value);

        // 0.01 is an agreed min value for validation representing 1 cent
        if (input < 0.01 || !input) {
            setGrowthAmountFeedback(i18n.t('widgets_growthChart_inputError'));
            return;
        }

        setGrowthAmountFeedback('');

        seriesData
            .filter((s: any) => s.type !== 'flags')
            .forEach((series: any, i: number) => {
                const firstPointIndex = series.data.findIndex((point: any) => point.x >= minX);
                // get the first point after adjust the nav
                const firstPoint = series.data[firstPointIndex];

                if (firstPoint === undefined) return;

                const rebasedData = rebaseGrowthChart(input, series.data, firstPoint).slice();
                // update the chart with the rebased values
                stockChartRef?.current?.chart?.series[i].update({ data: rebasedData });
            });
    };

    return (
        <>
            <div
                id={`${stockChartId}`}
                className={`js-stock-chart-tile ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${stockChartStyles}${theme
                        ? themeStockChartStyles
                        : ''}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
            >
                <HeaderWrapper headings={chartTitles} />
                {['pdf', 'pptx'].includes(documentType) ? (
                    <div id={`${stockChartId}-stock-chart`} />
                ) : (
                    <>
                        {seriesData && seriesData.length > 0 && highstock && (
                            <HighchartsReact
                                ref={stockChartRef}
                                highcharts={highstock}
                                constructorType={'stockChart'}
                                options={highChartOptions}
                                allowChartUpdate={true}
                                updateArgs={[true, true]}
                            />
                        )}
                        {rest.showGrowthAmount && (
                            <div className={'growth-amount'}>
                                <label
                                    htmlFor={`${stockChartId}-growth-amount-input`}
                                    className={'growth-chart-label'}
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.format(rest.growthAmountLabel || '', data),
                                    }}
                                />
                                <input
                                    className={'growth-amount-input'}
                                    id={`${stockChartId}-growth-amount-input`}
                                    type={'number'}
                                    min={0.01}
                                    onChange={onChangeAmount}
                                    placeholder={`${rest.initialGrowthAmount}`}
                                />
                                <div className="growth-amount-feedback">{growthAmountFeedback}</div>
                            </div>
                        )}
                        <div className={'custom-legend-wrapper'}>
                            <span
                                className={'legend-dropdown-title'}
                                dangerouslySetInnerHTML={{
                                    __html: i18n.format(rest.legendDropDownTitle || '', data),
                                }}
                            />
                            <div>
                                <select
                                    id={`${stockChartId}-customSelect`}
                                    className="custom-select"
                                    onChange={onChangeSeriesSelected}
                                >
                                    <option className="item">
                                        {i18n.t('widgets_growthChart_select')}
                                    </option>
                                    {stockChartRef?.current?.chart?.series
                                        .filter(
                                            (s: any) =>
                                                s.type === 'area' && !s.userOptions?.isCurrent
                                        )
                                        .map((s: { name: string }, i: number) => (
                                            <option className="item" value={i + 1} key={i}>
                                                {s.name}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {['pdf', 'pptx'].includes(documentType) && (
                <>
                    <script
                        id={`${stockChartId}-chart-options`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(highChartOptions),
                        }}
                    ></script>
                    <script
                        id={`${stockChartId}-json-data`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(seriesData) }}
                    ></script>
                </>
            )}
        </>
    );
};

export default StockChart;
